import { AppHTTPService } from '@/api/http-service'
import { ArtifactView } from '@/components/ArtifactView/ArtifactView'
import { EmptySheetState } from '@/components/EmptySheetState/EmptySheetState'
import { Artifact } from '@/components/Header/Header'
// Removed agent overlay components - progress now shown as chat messages
import { FieldsContext } from '@/contexts/FieldsContext'
import { useSheet } from '@/contexts/SheetContext'
import { useColumnSelectionContext } from '@flatfile/turntable'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePrepareChatTask } from '../../hooks'
// import { useAgentProgress } from '../../hooks/useAgentProgress' // No longer needed
import EnrichmentPanel from './enrichment'
import RulesPanel from './rules-panel'
import { ChatInterface } from './views/chat/ChatInterface'
import { MemoizedSheetViewContainer } from './views/sheet/SheetViewContainer'

// Inner component that will remount when sourceSheetId changes
export function ChatSheet({
  sourceSheetId,
  fileId,
  threadId,
  thread,
  hasNoSheets,
  projectId,
  artifact,
}: {
  sourceSheetId: string
  fileId: string
  threadId?: string
  thread?: any
  hasNoSheets?: boolean
  projectId?: string
  artifact?: Artifact | null
}) {
  const [inputMessage, setInputMessage] = useState('')
  const { setColumnSelections } = useColumnSelectionContext()
  const { sheet } = useSheet()
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [showEnrichment, setShowEnrichment] = useState(false)
  const [editingEnrichmentKey, setEditingEnrichmentKey] = useState<
    string | undefined
  >()
  const [showRules, setShowRules] = useState(false)
  const [editingRuleId, setEditingRuleId] = useState<string | undefined>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const handleSidebarCollapse = (collapsed: boolean) => {
    console.warn('collapse', collapsed)
    setIsSidebarCollapsed(!isSidebarCollapsed)
  }

  const handleEnrichmentEdit = (enrichmentKey: string) => {
    setEditingEnrichmentKey(enrichmentKey)
    setShowEnrichment(true)
  }

  const handleEnrichmentClose = () => {
    setShowEnrichment(false)
    setEditingEnrichmentKey(undefined)
  }

  const handleRuleEdit = (ruleId: string) => {
    setEditingRuleId(ruleId)
    setShowRules(true)
  }

  const handleRuleClose = () => {
    setShowRules(false)
    setEditingRuleId(undefined)
  }

  const { columnConfig } = useContext(FieldsContext)

  // Initialize chat task hook with threadId
  const {
    chatItems,
    isLoading,
    handleStartPrepareChat,
    handleOptionClick,
    operationItems,
    createOrGetThread,
    sendMessageToThread,
    addRevertOperation,
    statusText,
  } = usePrepareChatTask({
    sourceSheetId: sourceSheetId,
    userInstructions: inputMessage,
    threadId,
    projectId: projectId,
    artifactId: artifact?.id,
  })

  // Agent progress is now handled via chat messages instead of overlays

  //  Combine chat items from the hook and manual items
  const allOperations = useMemo(() => {
    return thread?.state?.operations
      ? [
          ...operationItems,
          ...(Array.isArray(thread.state.operations)
            ? thread.state.operations
            : [thread.state.operations]),
        ].filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
        )
      : [...operationItems]
  }, [operationItems, thread])

  const handleSendMessage = useCallback(
    async (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault()
      }

      setColumnSelections([])

      const trimmedMessage = inputMessage.trim()
      setInputMessage('')
      if (trimmedMessage) {
        try {
          // Send the message to the API
          handleStartPrepareChat(trimmedMessage)
        } catch (error) {
          console.error('Error sending message:', error)
          setInputMessage('')
        }
      }
    },
    [
      inputMessage,
      createOrGetThread,
      handleStartPrepareChat,
      setColumnSelections,
    ]
  )

  const mentions = useMemo(() => {
    return (
      columnConfig?.map((field) => ({
        ref: `@{{{"type":"field","name":"${field.value}","displayText":"${field.label}","groupName":"Default","groupId":"${sourceSheetId}"}}}`,
        displayText: field.label,
        name: field.value,
        type: 'field',
        groupName: 'Default',
        groupId: sourceSheetId,
      })) || []
    )
  }, [sheet, sourceSheetId, columnConfig])

  const reroll = useCallback(
    async (messageId: string) => {
      const messageIdFormatted = messageId.split('-options')[0]
      try {
        await AppHTTPService.client.agent.suggestReroll({
          sheetId: sourceSheetId,
          threadId: threadId,
          userPrompt: inputMessage,
          messageId: messageIdFormatted,
          columnConfig,
        })
      } catch (error) {
        console.error('Error rerolling:', error)
      }
    },
    [sourceSheetId, threadId, inputMessage]
  )

  // Handle suggestion clicks from AgentSummaryCard
  const handleSuggestionClick = useCallback(
    (suggestion: any) => {
      // Execute the suggestion as if it were a user message
      setInputMessage(suggestion.action)
      handleStartPrepareChat(suggestion.action)
    },
    [handleStartPrepareChat]
  )

  // Removed agent progress handling - now done via chat messages

  return (
    <div className='relative h-full w-full'>
      <div className='flex h-full w-full transition-all duration-300'>
        <div
          className={`transition-all duration-300 overflow-hidden h-full ${!isSidebarCollapsed && !showEnrichment && !showRules ? 'w-[424px] opacity-100 pointer-events-auto' : 'w-0 opacity-0 pointer-events-none'}`}
          style={{ minWidth: 0 }}
        >
          <ChatInterface
            allChatItems={chatItems}
            inputMessage={inputMessage}
            setInputMessage={setInputMessage}
            handleSendMessage={handleSendMessage}
            isLoading={isLoading}
            handleOptionClick={handleOptionClick}
            operationItems={allOperations || []}
            mentions={mentions || []}
            addUserMessage={sendMessageToThread}
            threadId={threadId}
            statusText={statusText}
            addRevertOperation={addRevertOperation}
            //reroll={() => reroll(chatItems[chatItems.length - 1].id)}
            isSidebarCollapsed={isSidebarCollapsed}
            handleSidebarCollapse={handleSidebarCollapse}
          />
        </div>
        {/* Sheet view or Artifact view: always present, flex-1, flush left if chat is collapsed or enrichment is open */}
        <div className='flex-1 min-w-0'>
          {artifact ? (
            <ArtifactView artifact={artifact} />
          ) : hasNoSheets ? (
            <EmptySheetState
              projectId={projectId || ''}
              onSheetCreated={() => {
                // Refresh the data
                queryClient.invalidateQueries({
                  queryKey: ['files', projectId],
                })
                queryClient.invalidateQueries({ queryKey: ['sheets'] })
                // Navigate to refresh the page with the new sheet
                window.location.reload()
              }}
            />
          ) : (
            <MemoizedSheetViewContainer
              sourceSheetId={sourceSheetId}
              fileId={fileId}
              isSidebarCollapsed={isSidebarCollapsed}
              handleSidebarCollapse={handleSidebarCollapse}
              showEnrichment={showEnrichment}
              setShowEnrichment={setShowEnrichment}
              onEnrichmentEdit={handleEnrichmentEdit}
              showRules={showRules}
              setShowRules={setShowRules}
              onRuleEdit={handleRuleEdit}
            />
          )}
        </div>
        <div
          className={`transition-all duration-300 overflow-hidden bg-white border-l border-gray-200 h-full ${showEnrichment ? 'w-[424px]' : 'w-0'}`}
          style={{ minWidth: 0 }}
        >
          {showEnrichment && (
            <EnrichmentPanel
              onClose={handleEnrichmentClose}
              columnConfig={columnConfig}
              sheetId={sourceSheetId}
              editEnrichmentKey={editingEnrichmentKey}
            />
          )}
        </div>
        <div
          className={`transition-all duration-300 overflow-hidden bg-white border-l border-gray-200 h-full ${showRules ? 'w-[424px]' : 'w-0'}`}
          style={{ minWidth: 0 }}
        >
          {showRules && (
            <RulesPanel
              onClose={handleRuleClose}
              sheetId={sourceSheetId}
              editRuleId={editingRuleId}
            />
          )}
        </div>
      </div>
    </div>
  )
}
